import { IMap } from "@/Common/Models/IMap";

/**
 * A key/value pair that can be set or retrieved.
 */
class KeyValuePair<TKey, TValue> {
    /**
     * The key which identifies the value. This is usually unique within a list.
     */
    public Key: TKey;

    /**
     * The value associated with the {@link Key}.
     */
    public Value: TValue;

    /**
     * Whether this key/value pair is active (e.g. selectable in a dropdown).
     */
    public Active: boolean;

    /**
     * Construct an instance.
     * @param key The key which identifies the value. This is usually unique within a list.
     * @param value The value associated with the {@link key}.
     * @param active Whether this key/value pair is active (e.g. selectable in a dropdown).
     */
    constructor(key: TKey, value: TValue, active: boolean = true) {
        this.Key = key;
        this.Value = value;
        this.Active = active;
    }

    /**
     * Build a list of string/string key/value pairs from a map which contains either string values, or objects with a `Label` field.
     * @param map The map to build a list from.
     * @param sortAlphabetically Whether to sort the key/value pairs alphabetically by value.
     */
    public static fromMapOfStrings(map?: IMap<string | { Label: string }>, sortAlphabetically: boolean = true): KeyValuePair<string, string>[] {
        const pairs = Object
            .keys(map ?? {})
            .map(key => new KeyValuePair(
                key,
                typeof map[key] === "string"
                    ? map[key]
                    : map[key]["Label"]
            ));

        return sortAlphabetically
            ? pairs.sortByProperty(x => x.Value.toLowerCase())
            : pairs;
    }
}

export { KeyValuePair };
